import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import type { PersistQueryClientOptions } from '@tanstack/react-query-persist-client';
import { del, get, set } from 'idb-keyval';

const IDB_CACHE_KEY = 'fintecy-react-query-cache';

/** Bump when the persisted shape changes incompatibly; busts every on-disk cache. */
const CACHE_SCHEMA_VERSION = 'v1';

/** Keep last-known data for up to a week of the app not being opened. */
export const QUERY_CACHE_MAX_AGE = 1000 * 60 * 60 * 24 * 7;

/**
 * Query-key heads whose data must never touch disk: live/short-poll feeds that
 * are meaningless when stale (`/health`, notifications) and sensitive auth state.
 * Keys are URL-path strings (Orval) or hand-rolled tuples like `['notifications']`.
 */
const NON_PERSISTED_KEY_HEADS = ['/health', '/api/auth', 'notifications'];

export function shouldPersistQuery(queryKey: readonly unknown[]): boolean {
  const head = String(queryKey[0] ?? '');
  return !NON_PERSISTED_KEY_HEADS.some((blocked) => head.startsWith(blocked));
}

function createIdbPersister() {
  return createAsyncStoragePersister({
    key: IDB_CACHE_KEY,
    throttleTime: 1000,
    storage: {
      getItem: (key) => get<string>(key),
      setItem: (key, value) => set(key, value),
      removeItem: (key) => del(key),
    },
  });
}

/**
 * A deploy (schema bump) or a login as a different user changes the buster, so the
 * persisted snapshot is discarded and overwritten rather than retaining previous
 * versions or bleeding one user's data into another's session.
 */
export function cacheBuster(userId: string | null): string {
  return `${CACHE_SCHEMA_VERSION}:${userId ?? 'anon'}`;
}

export function buildPersistOptions(
  userId: string | null
): Omit<PersistQueryClientOptions, 'queryClient'> {
  return {
    persister: createIdbPersister(),
    maxAge: QUERY_CACHE_MAX_AGE,
    buster: cacheBuster(userId),
    dehydrateOptions: {
      shouldDehydrateQuery: (query) =>
        query.state.status === 'success' && shouldPersistQuery(query.queryKey),
    },
  };
}
